import type { paths } from "@src/packages/openapi/schema";
import type { CanonicalExperienceContext } from "./canonicalExperienceContext";

type CycleCreateBody = NonNullable<
  paths["/{tenantId}/cycles"]["post"]["requestBody"]
>["content"]["application/json"];

export type CycleContextSnapshot = NonNullable<
  CycleCreateBody["contextSnapshot"]
>;

type CycleContextSource = Pick<
  CanonicalExperienceContext,
  "intent" | "acceptedTreatmentPlan" | "familyBuildingCases"
>;

function uniqueIds(values: readonly (string | null | undefined)[]): string[] {
  return [...new Set(values.filter((value): value is string => !!value))];
}

/**
 * Capture the server-owned context that was visible when a treatment cycle
 * started. Unknown raw values remain in rawValues; no cycle type is inferred
 * from sex, gender, or relationship state.
 */
export function buildCycleContextSnapshot(
  context: CycleContextSource,
  materialIds: readonly string[] = [],
): CycleContextSnapshot {
  const plan = context.acceptedTreatmentPlan;
  const participantIds = uniqueIds(
    context.familyBuildingCases.flatMap((familyCase) =>
      familyCase.status === "active"
        ? familyCase.participants
            .filter(
              (participant) =>
                participant.status === "active" &&
                participant.accessRole !== "no_access",
            )
            .map((participant) => participant.participantPatientId)
        : [],
    ),
  );

  return {
    acceptedTreatmentPlanId: plan?.id ?? null,
    acceptedTreatmentPlanVersion: plan?.version ?? null,
    clinicalSubjectPatientId: plan?.patientId ?? null,
    participantIds,
    materialIds: uniqueIds(materialIds),
    status: plan?.status === "accepted" ? "captured" : "missing",
    rawValues: {
      primaryIntent: context.intent.primaryIntent,
      goal: context.intent.goal,
      stage: context.intent.stage,
      partnerChoice: context.intent.partnerChoice,
      plannedModalities: context.intent.plannedModalities,
      acceptedTreatmentFamily: plan?.treatmentFamily ?? null,
      treatmentDestination: plan?.treatmentDestination ?? null,
      ovulationApproach: plan?.ovulationApproach ?? null,
      fertilizationMethod: plan?.fertilizationMethod ?? null,
      eggSource: plan?.eggSource ?? null,
      eggState: plan?.eggState ?? null,
      spermSource: plan?.spermSource ?? null,
      spermState: plan?.spermState ?? null,
      transferPlan: plan?.transferPlan ?? null,
      pgtPlan: plan?.pgtPlan ?? null,
      decisionSource: plan?.decisionSource ?? null,
    },
    snapshotSource: "start-treatment",
  };
}
